
import { proxyRequest, fallbackProxyRequest } from './proxyService';

interface ReplicateImageParams {
  gender?: string;
  ethnicity?: string; 
  age?: number;
  displayName: string;
}

// Function to directly call our Replicate edge function
const callReplicateApi = async <T>(endpoint: string, method: string, body?: any): Promise<T> => {
  console.log(`🔄 Calling Replicate API (${method}) on ${endpoint}`);
  console.log(`Request body:`, body);
  
  try {
    // URL of our Replicate edge function
    const edgeFunctionUrl = 'https://klsdmuldpwprmosyhxax.supabase.co/functions/v1/replicate-api';
    
    // Make the request to our edge function
    const response = await fetch(edgeFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        endpoint,
        method,
        body
      })
    });
    
    // First, get the raw response text for debugging
    const responseText = await response.text();
    console.log(`Raw response from edge function (${responseText.length} chars):`);
    console.log(responseText.substring(0, 200) + (responseText.length > 200 ? '...' : ''));
    
    // Try to parse as JSON
    let responseData;
    try {
      responseData = JSON.parse(responseText);
    } catch (e) {
      console.error('Failed to parse response as JSON:', e);
      throw new Error(`Invalid JSON response from edge function: ${responseText.substring(0, 100)}`);
    }
    
    if (!response.ok) {
      // Enhanced error logging
      console.error(`❌ Edge function request failed with status ${response.status}:`, responseData);
      
      // Special handling for auth errors
      if (response.status === 401 || (responseData.error && responseData.error.includes('auth'))) {
        throw new Error(`Authentication failed with Replicate API. Please check your API key in Supabase secrets. Details: ${JSON.stringify(responseData)}`);
      }
      
      throw new Error(`Edge function request failed with status ${response.status}: ${JSON.stringify(responseData)}`);
    }
    
    console.log("Replicate API response (success):", responseData);
    return responseData as T;
  } catch (error) {
    console.error('❌ Replicate API call failed:', error);
    throw error;
  }
};

export const generateProfileImage = async (params: ReplicateImageParams): Promise<string> => {
  const { gender, ethnicity, age, displayName } = params;
  
  // Create a normalized gender term for the prompt
  let genderTerm = 'person';
  if (gender === 'female') genderTerm = 'woman';
  if (gender === 'male') genderTerm = 'man';
  
  // Default to young adult if no age provided
  const ageDescription = age ? (age < 30 ? 'young' : age < 50 ? 'mature' : 'older') : 'young';
  
  // Create a prompt for Stable Diffusion
  const ethnicityText = ethnicity ? `${ethnicity} ` : '';
  const prompt = `RAW photo, a portrait photo of a ${ethnicityText}${genderTerm} ${ageDescription}, elegant, sensual pose, studio lighting, fashionable clothes, perfect makeup, 8k uhd, high quality film grain, Fujifilm XT3`;
  
  console.log(`Generating image for ${displayName} with prompt: ${prompt}`);
  
  // Model to use - Realistic Vision v5.1
  const model = "lucataco/realistic-vision-v5.1:2c8e954decbf70b7607a4414e5785ef9e4de4b8c51d50fb8b8b349160e0ef6bb";
  
  try {
    // Start a new prediction via our edge function
    const predictionResponse = await callReplicateApi<any>(
      'predictions', 
      'POST', 
      {
        version: model.split(':')[1],
        input: {
          prompt: prompt,
          width: 512,
          height: 728,
          seed: Math.floor(Math.random() * 10000),
          steps: 20,
          guidance: 5,
          scheduler: "EulerA",
          negative_prompt: "(deformed iris, deformed pupils, semi-realistic, cgi, 3d, render, sketch, cartoon, drawing, anime:1.4), text, close up, cropped, out of frame, worst quality, low quality, jpeg artifacts, ugly, duplicate, morbid, mutilated, extra fingers, mutated hands, poorly drawn hands, poorly drawn face, mutation, deformed, blurry, dehydrated, bad anatomy, bad proportions, extra limbs, cloned face, disfigured, gross proportions, malformed limbs, missing arms, missing legs, extra arms, extra legs, fused fingers, too many fingers, long neck"
        }
      }
    );
    
    console.log("Prediction response:", predictionResponse);
    
    // If we get direct output (when using 'Prefer: wait' header)
    if (predictionResponse.output && predictionResponse.output.length > 0) {
      console.log("Direct output available:", predictionResponse.output[0]);
      return predictionResponse.output[0];
    }
    
    // If prediction is started but needs polling, wait for the result
    if (predictionResponse.id) {
      return await pollReplicatePrediction(predictionResponse.id);
    } else {
      throw new Error('Failed to start prediction: ' + JSON.stringify(predictionResponse));
    }
  } catch (error) {
    console.error(`❌ Failed to generate image for ${displayName}:`, error);
    throw error;
  }
};

// Periodically check the prediction status
const pollReplicatePrediction = async (id: string): Promise<string> => {
  const maxAttempts = 20;
  const checkInterval = 2000; // 2 seconds
  
  console.log(`Starting to poll for prediction ${id}`);
  
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      console.log(`Polling attempt ${attempt + 1}/${maxAttempts} for prediction ${id}`);
      
      // Check status via our edge function
      const data = await callReplicateApi<any>(`predictions/${id}`, 'GET');
      
      console.log(`Polling attempt ${attempt + 1}, status: ${data.status}`, data);
      
      // Check if prediction is complete
      if (data.status === 'succeeded') {
        // Return the first image in the output
        console.log(`✅ Prediction ${id} succeeded after ${attempt + 1} attempts!`);
        return data.output[0] || '';
      } else if (data.status === 'failed') {
        console.error(`❌ Prediction ${id} failed after ${attempt + 1} attempts:`, data.error || data);
        throw new Error('Prediction failed: ' + (data.error || JSON.stringify(data)));
      }
      
      // Wait before checking again
      console.log(`Waiting ${checkInterval}ms before next polling attempt...`);
      await new Promise(resolve => setTimeout(resolve, checkInterval));
    } catch (error) {
      console.error(`Error checking status for prediction ${id} (attempt ${attempt + 1}):`, error);
      
      // Wait before checking again anyway
      await new Promise(resolve => setTimeout(resolve, checkInterval));
    }
  }
  
  console.error(`❌ Timeout exceeded for prediction ${id} after ${maxAttempts} attempts`);
  throw new Error('Timeout exceeded: Image generation is taking too long');
};
